import { AuditOutlined, ImportOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Drawer, Flex, Input, Modal, Space, Table, Tag, Timeline, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { SkuSelect } from "../catalog/mapping";
import { NoWms, useWmsWarehouse } from "./common";
import { SerialImportModal, parseSerials } from "./SerialImport";

type Serial = Schemas["Serial"];
const STATUSES = ["in_stock", "shipped", "removed", "all"] as const;
const STATUS_COLOR: Record<Serial["status"], string> = { in_stock: "success", shipped: "processing", removed: "default" };
const KINDS = ["received", "shipped", "returned", "transfer_out", "transfer_in", "count_loss", "count_gain", "vendor_return", "disposed"] as const;

/**
 * Gudang > IMEI / SN (Plan §6.21): every serial number of the warehouse with
 * where it came from and where it went, its history, the export filtered by
 * status and by the dates it came in or left, the audit of a scan against the
 * books, and the import of serials for stock already on hand.
 */
export function Serials() {
  const { t, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { current, loading } = useWmsWarehouse();
  const tz = me?.workspace?.timezone;
  const status = useStatusFilter(STATUSES, "all");
  const search = useSearchFilter();
  const inRange = useDateRange("inFrom", "inTo");
  const outRange = useDateRange("outFrom", "outTo");
  const page = useListPage();
  const [open, setOpen] = useState<string | null>(null);
  const [auditing, setAuditing] = useState(false);
  const [registering, setRegistering] = useState(false);

  const filters = {
    warehouseId: current?.id ?? "",
    ...(status.value !== "all" ? { status: status.value } : {}),
    ...search.query,
    ...(inRange.query.from ? { receivedFrom: inRange.query.from } : {}),
    ...(inRange.query.to ? { receivedTo: inRange.query.to } : {}),
    ...(outRange.query.from ? { leftFrom: outRange.query.from } : {}),
    ...(outRange.query.to ? { leftTo: outRange.query.to } : {}),
  };
  const list = useQuery({
    queryKey: ["serials", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/serials", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    placeholderData: keepPreviousData,
  });
  const ref = (kind: string, number: string) => (number ? `${t(`serial.kind.${kind}` as "serial.kind.received")} · ${number}` : "");

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("serial.title")}
        </Typography.Title>
        <Space wrap>
          <Button icon={<AuditOutlined />} onClick={() => setAuditing(true)}>
            {t("serial.audit.button")}
          </Button>
          {can("warehouse.manage") && (
            <Button icon={<ImportOutlined />} onClick={() => setRegistering(true)}>
              {t("serial.register.button")}
            </Button>
          )}
        </Space>
      </Flex>
      <Typography.Paragraph type="secondary">{t("serial.help")}</Typography.Paragraph>
      <Flex gap={12} wrap style={{ marginBottom: 16 }} align="center">
        <SearchFilter filter={search} placeholder={t("serial.search")} />
        <StatusFilter filter={status} options={STATUSES.map((s) => ({ value: s, label: t(`serial.status.${s}`) }))} />
        <Space size={4}>
          <Typography.Text type="secondary">{t("serial.inDate")}</Typography.Text>
          <DateRangeFilter range={inRange} />
        </Space>
        <Space size={4}>
          <Typography.Text type="secondary">{t("serial.outDate")}</Typography.Text>
          <DateRangeFilter range={outRange} />
        </Space>
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/serials/export"
          query={filters}
          filename={t("serial.title")}
          columns={[
            { key: "serialNo", label: t("serial.number") },
            { key: "skuCode", label: t("catalog.code") },
            { key: "skuName", label: t("common.name") },
            { key: "status", label: t("common.status"), values: { in_stock: t("serial.status.in_stock"), shipped: t("serial.status.shipped"), removed: t("serial.status.removed") } },
            { key: "warehouseName", label: t("orders.warehouse") },
            { key: "receivedAt", label: t("serial.inDate"), kind: "datetime" },
            { key: "receivedRef", label: t("serial.inRef") },
            { key: "leftAt", label: t("serial.outDate"), kind: "datetime" },
            { key: "leftKind", label: t("serial.outKind"), values: Object.fromEntries(KINDS.map((k) => [k, t(`serial.kind.${k}`)])) },
            { key: "leftRef", label: t("serial.outRef") },
          ]}
        />
      </Flex>
      <Table<Serial>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("serial.empty") }}
        onRow={(s) => ({ onClick: () => setOpen(s.id), style: { cursor: "pointer" } })}
        columns={[
          { title: t("serial.number"), dataIndex: "serialNo", render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("catalog.code"),
            dataIndex: "skuCode",
            render: (v: string, s) => (
              <Space orientation="vertical" size={0}>
                <span className="z-num">{v}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {s.skuName}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (v: Serial["status"]) => <Tag color={STATUS_COLOR[v]}>{t(`serial.status.${v}`)}</Tag> },
          {
            title: t("serial.inDate"),
            width: 200,
            render: (_, s) =>
              s.receivedAt ? (
                <Space orientation="vertical" size={0}>
                  {formatDateTime(s.receivedAt, tz)}
                  <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                    {s.receivedRef}
                  </Typography.Text>
                </Space>
              ) : (
                "—"
              ),
          },
          {
            title: t("serial.outDate"),
            width: 220,
            render: (_, s) =>
              s.leftAt ? (
                <Space orientation="vertical" size={0}>
                  {formatDateTime(s.leftAt, tz)}
                  <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                    {ref(s.leftKind, s.leftRef) || t(`serial.kind.${s.leftKind as "shipped"}`)}
                  </Typography.Text>
                </Space>
              ) : (
                "—"
              ),
          },
        ]}
      />
      <SerialDrawer id={open} onClose={() => setOpen(null)} />
      {current && auditing && <AuditModal warehouseId={current.id} onClose={() => setAuditing(false)} />}
      {current && registering && <RegisterModal warehouseId={current.id} onClose={() => setRegistering(false)} />}
    </>
  );
}

function SerialDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const serial = useQuery({
    queryKey: ["serials", "one", id],
    queryFn: () => unwrap(api.GET("/v1/serials/{serialID}", { params: { path: { serialID: id! } } })),
    enabled: !!id,
  });
  const s = serial.data;
  return (
    <Drawer open={!!id} onClose={onClose} size={480} title={s ? <span className="z-num">{s.serialNo}</span> : t("serial.title")} loading={serial.isPending}>
      {s && (
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          <Space wrap>
            <Tag color={STATUS_COLOR[s.status]}>{t(`serial.status.${s.status}`)}</Tag>
            <span className="z-num">{s.skuCode}</span>
            <Typography.Text type="secondary">{s.skuName}</Typography.Text>
          </Space>
          {s.warehouseName && <Typography.Text>{t("serial.inWarehouse", { name: s.warehouseName })}</Typography.Text>}
          <Timeline
            items={s.events.map((e) => ({
              color: ["shipped", "transfer_out", "vendor_return", "count_loss"].includes(e.kind) ? "blue" : "green",
              children: (
                <Space orientation="vertical" size={0}>
                  <Typography.Text strong>
                    {t(`serial.kind.${e.kind}`)}
                    {e.refNumber ? <span className="z-num"> · {e.refNumber}</span> : null}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {[formatDateTime(e.at, tz), e.warehouseName, e.actorName].filter(Boolean).join(" · ")}
                  </Typography.Text>
                  {e.note && <Typography.Text style={{ fontSize: 12 }}>{e.note}</Typography.Text>}
                </Space>
              ),
            }))}
          />
        </Space>
      )}
    </Drawer>
  );
}

/** Audit IMEI: scan every phone on the floor, then compare with the books. */
function AuditModal({ warehouseId, onClose }: { warehouseId: string; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const errorText = useErrorText();
  const { message } = App.useApp();
  const [text, setText] = useState("");
  const [sku, setSku] = useState<string | undefined>();
  const scanned = parseSerials(text);
  const audit = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/serials/audit", { body: { warehouseId, serials: scanned, ...(sku ? { skuId: sku } : {}) } })),
    onError: (err) => void message.error(errorText(err)),
  });
  const r = audit.data;
  const block = (title: string, type: "success" | "warning" | "error" | "info", items: string[]) =>
    items.length > 0 && (
      <Alert
        type={type}
        showIcon
        title={`${title} (${formatNumber(items.length)})`}
        description={<Typography.Paragraph className="z-num" style={{ margin: 0, maxHeight: 160, overflow: "auto" }} copyable={{ text: items.join("\n") }}>{items.join(", ")}</Typography.Paragraph>}
      />
    );
  return (
    <Modal open title={t("serial.audit.title")} width={720} onCancel={onClose} footer={null} destroyOnHidden>
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("serial.audit.help")}</Typography.Text>
        <SkuSelect value={sku} onChange={setSku} placeholder={t("serial.audit.allSkus")} />
        <Input.TextArea autoFocus rows={8} className="z-num" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("serial.var.placeholder")} />
        <Flex gap={12} align="center">
          <Button type="primary" disabled={scanned.length === 0} loading={audit.isPending} onClick={() => audit.mutate()}>
            {t("serial.audit.run", { n: formatNumber(scanned.length) })}
          </Button>
          {sku && <Button onClick={() => setSku(undefined)}>{t("serial.audit.allSkus")}</Button>}
        </Flex>
        {r && (
          <Space orientation="vertical" size={8} style={{ width: "100%" }}>
            <Alert type="success" showIcon title={t("serial.audit.matched", { n: formatNumber(r.matched) })} />
            {block(t("serial.audit.missing"), "warning", r.missing.map((m) => `${m.serialNo} (${m.skuCode})`))}
            {block(t("serial.audit.unknown"), "error", r.unknown)}
            {block(t("serial.audit.elsewhere"), "info", r.elsewhere.map((e) => `${e.serialNo} (${t(`serial.status.${e.status as Serial["status"]}`)}${e.warehouseName ? ` · ${e.warehouseName}` : ""})`))}
            <Typography.Text type="secondary">{t("serial.audit.after")}</Typography.Text>
          </Space>
        )}
      </Space>
    </Modal>
  );
}

/** Serials of stock already on hand (from before Zerpid, or a receipt without its list). */
function RegisterModal({ warehouseId, onClose }: { warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const [sku, setSku] = useState<string | undefined>();
  if (!sku) {
    return (
      <Modal open title={t("serial.register.button")} onCancel={onClose} footer={null} destroyOnHidden>
        <Typography.Paragraph type="secondary">{t("serial.register.pick")}</Typography.Paragraph>
        <SkuSelect value={sku} onChange={setSku} autoFocus singleOnly />
      </Modal>
    );
  }
  return (
    <SerialImportModal
      title={t("serial.register.button")}
      help={t("serial.register.help")}
      onClose={onClose}
      onSubmit={async (serials) => {
        const r = await unwrap(api.POST("/v1/serials/register", { body: { warehouseId, skuId: sku, serials } }));
        void message.success(t("serial.import.done", { n: r.registered }));
      }}
    />
  );
}
